/* SIZESOFTX
=========================================================================================
* Descripción: Overlay visual para selección de objetos con cámara IA
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1        | 06/06/2026 | Lic. José Luis Aragón Cervantes  | Instrucción configurable para buscadores y entrenamiento IA
* ---------------------------------------------------------------------------------------
*/

import { Box, Typography } from "@mui/material";
import { getVideoDisplayBox } from "./utils/cameraSelection";

export default function CameraObjectSelectorOverlay({
    selection,
    disabled = false,
    videoElement = null,
    label = "Objeto IA",
    instructionLabel = "Dibuje la envoltura del objeto",
}) {
    if (disabled) return null;

    if (!selection) {
        return (
            <Box
                sx={{
                    position: "absolute",
                    left: 12,
                    bottom: 12,
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 999,
                    bgcolor: "rgba(0,0,0,.62)",
                    color: "#fff",
                    border: "1px solid rgba(255,255,255,.45)",
                    pointerEvents: "none",
                }}
            >
                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                    {instructionLabel}
                </Typography>
            </Box>
        );
    }

    if (selection.contourPoints?.length > 1) {
        const displayBox = getVideoDisplayBox(videoElement);
        const projectPoint = (point) => ({
            xPct: displayBox.xPct + point.xPct * displayBox.widthPct,
            yPct: displayBox.yPct + point.yPct * displayBox.heightPct,
        });
        const points = selection.contourPoints
            .map(point => {
                const projected = projectPoint(point);
                return `${projected.xPct * 100},${projected.yPct * 100}`;
            })
            .join(" ");
        const projectedPoints = selection.contourPoints.map(projectPoint);
        const minXPct = Math.min(...projectedPoints.map(point => point.xPct));
        const maxXPct = Math.max(...projectedPoints.map(point => point.xPct));
        const minYPct = Math.min(...projectedPoints.map(point => point.yPct));
        const labelXPct = Math.min(0.92, Math.max(0.08, minXPct + ((maxXPct - minXPct) / 2)));
        const labelYPct = Math.max(0.01, minYPct);

        return (
            <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
                <svg
                    viewBox="0 0 100 100"
                    preserveAspectRatio="none"
                    style={{
                        position: "absolute",
                        inset: 0,
                        width: "100%",
                        height: "100%",
                        overflow: "visible",
                    }}
                >
                    {selection.debugRays?.map((ray, index) => (
                        (() => {
                            const start = projectPoint({ xPct: ray.x1Pct, yPct: ray.y1Pct });
                            const end = projectPoint({ xPct: ray.x2Pct, yPct: ray.y2Pct });
                            return (
                                <line
                                    key={`debug-ray-${index}`}
                                    x1={start.xPct * 100}
                                    y1={start.yPct * 100}
                                    x2={end.xPct * 100}
                                    y2={end.yPct * 100}
                                    stroke={ray.color}
                                    strokeWidth="0.9"
                                    vectorEffect="non-scaling-stroke"
                                    strokeLinecap="round"
                                    opacity="0.65"
                                />
                            );
                        })()
                    ))}
                    {selection.isDrawing ? (
                        <polyline
                            points={points}
                            fill="none"
                            stroke="#ffd600"
                            strokeWidth="3"
                            vectorEffect="non-scaling-stroke"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            filter="drop-shadow(0 0 5px rgba(255,214,0,.95))"
                        />
                    ) : (
                        <polygon
                            points={points}
                            fill="rgba(255, 214, 0, .10)"
                            stroke="#ffd600"
                            strokeWidth="3"
                            vectorEffect="non-scaling-stroke"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            filter="drop-shadow(0 0 5px rgba(255,214,0,.95))"
                        />
                    )}
                </svg>
                <Box
                    sx={{
                        position: "absolute",
                        left: `${labelXPct * 100}%`,
                        top: `${labelYPct * 100}%`,
                        transform: "translate(-50%, calc(-100% - 6px))",
                        px: 1,
                        py: 0.25,
                        borderRadius: 999,
                        bgcolor: "#ffd600",
                        color: "#111",
                        whiteSpace: "nowrap",
                        fontSize: 11,
                        fontWeight: 800,
                        boxShadow: 2,
                    }}
                >
                    {label || "Objeto IA"}
                </Box>
            </Box>
        );
    }

    return null;
}
